/// <reference types="vite/client" />

import { Link, useNavigate } from 'react-router-dom'
import { CredentialResponse, GoogleLogin } from '@react-oauth/google'
import React, { type FormEvent } from 'react'
import { AuthShell } from './AuthShell'
import { apiPost } from '../../lib/api'
import { AUTH_GOOGLE, AUTH_LOGIN } from '../../lib/endpoints'
import { saveAuth } from '../../lib/authStorage'
import { colors } from '../../config/themeAuth'

type AuthResponse = {
  accessToken: string
  refreshToken: string
  role: string
  onboardingCompleted?: boolean
}

export default function SignIn() {
  const navigate = useNavigate()
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const canSubmit = React.useMemo(
    () => email.trim().length > 3 && password.length >= 6,
    [email, password]
  )

  async function handleEmailLogin(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await apiPost<AuthResponse, { email: string; password: string }>(
        AUTH_LOGIN,
        {
          email: email.trim(),
          password,
        }
      )
      saveAuth({ accessToken: res.accessToken, refreshToken: res.refreshToken, role: res.role })
      const dest = res.role === 'ADMIN' || res.onboardingCompleted ? '/dashboard' : '/'
      navigate(dest, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogleSuccess(credentialResponse: CredentialResponse) {
    const idToken = credentialResponse.credential
    if (!idToken) {
      setError('Google sign-in did not return a token. Please try again.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const res = await apiPost<AuthResponse, { idToken: string }>(AUTH_GOOGLE, {
        idToken,
      })
      saveAuth({ accessToken: res.accessToken, refreshToken: res.refreshToken, role: res.role })
      const dest = res.role === 'ADMIN' || res.onboardingCompleted ? '/dashboard' : '/'
      navigate(dest, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in with email or Google to continue.">
      <form onSubmit={handleEmailLogin} className="space-y-4">
        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Email
          </label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Password
          </label>
          <input
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
            type="password"
            autoComplete="current-password"
            placeholder="Your password"
            required
          />
        </div>

        {error ? (
          <div
            className="rounded-2xl border px-4 py-3 text-sm"
            style={{
              borderColor: colors.border,
              color: colors.darkOrange,
              backgroundColor: colors.sand,
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="text-right text-sm" style={{ color: colors.muted }}>
          <Link to="/forgot-password" className="font-semibold underline" style={{ color: colors.ink }}>
            Forgot password?
          </Link>
        </p>
      </form>

      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1" style={{ backgroundColor: colors.border }} />
        <span className="text-xs" style={{ color: colors.muted }}>
          or
        </span>
        <div className="h-px flex-1" style={{ backgroundColor: colors.border }} />
      </div>

      {googleClientId ? (
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-in failed')}
            theme="outline"
            size="large"
            shape="pill"
            text="continue_with"
            logo_alignment="left"
            width={360}
          />
        </div>
      ) : (
        <div
          className="rounded-2xl border px-4 py-3 text-xs"
          style={{ borderColor: colors.border, color: colors.muted }}
        >
          Google sign-in is not configured. Set <code>VITE_GOOGLE_CLIENT_ID</code> to enable it.
        </div>
      )}

      <p className="mt-6 text-sm" style={{ color: colors.muted }}>
        New here?{' '}
        <Link to="/signup" className="font-semibold underline" style={{ color: colors.ink }}>
          Create an account
        </Link>
      </p>
    </AuthShell>
  )
}
